import React, { FC, useEffect, useMemo, useRef, useState } from "react";
import clsx from "clsx";
import { makeStyles } from "@material-ui/core/styles";
import { Avatar, Link } from "@material-ui/core";
import OverflowTooltip from "../../../components/OverflowTooltip/OverflowTooltip";
import FontIcon from "../../../components/FontIcon";
import { urlNormalizer } from "../../../helpers";
import { getFileSize } from "../../academic/modals/helpers/helperFunctions";
import { IMessageItemProps } from "../types";
import BlinkingDot from "../../../components/BlinkingDot";
import { useAuth } from "../../auth/hooks/useAuth";
import { UserRole } from "../../user/enums/UserRole";

const useStyles = makeStyles(() => ({
  root: {
    "& + &": {
      marginTop: 10,
    },
  },
  message: {
    display: "flex",
    columnGap: "8px",
  },
  myMessage: {
    flexDirection: "row-reverse",
  },
  avatar: {
    width: 30,
    height: 30,
  },
  groupContainer: {
    "& :nth-child(n) > div": {
      borderRadius: "0 16px 16px 0",
    },
    "& :first-child > div": {
      borderRadius: "16px 16px 16px 0",
    },
    "& :last-child > div": {
      borderRadius: "0 16px 16px 16px",
    },
  },
  myGroupContainer: {
    "& :nth-child(n) > div": {
      borderRadius: "16px 0 0 16px",
    },
    "& :first-child > div": {
      borderRadius: "16px 16px 0 16px",
    },
    "& :last-child > div": {
      borderRadius: "16px 0 16px 16px",
    },
  },
  groupSubContainer: {
    "& + &": {
      marginTop: 4,
    },
  },
  myGroupSubContainer: {
    display: "flex",
    justifyContent: "flex-end",
  },
  groupItem: {
    display: "inline-block",
    backgroundColor: "#FFC10D66",
  },
  myGroupItem: {
    backgroundColor: "#EEEFF3",
  },
  template: {
    wordBreak: "break-word",
    whiteSpace: "pre-wrap",
    maxWidth: 348,
    padding: "12px 16px",
    borderRadius: 16,
  },
  fileContainer: {
    display: "flex",
    columnGap: 8,
  },
  fileIcon: {
    marginTop: 10,
    color: "#192130",
    fontSize: 18,
  },
  myFileIcon: {
    color: "#858997",
  },
  fileName: {
    maxWidth: 290,
    fontSize: 14,
    color: "#192130",
    "&:hover": {
      cursor: "pointer",
      textDecoration: "underLine",
    },
  },
  fileSize: {
    fontSize: 12,
    color: "#918769",
  },
  myFileSize: {
    color: "#858997",
  },
  date: {
    padding: "0 38px",
    fontSize: 14,
    color: "#858997",
    marginTop: 2,
  },
  myDate: {
    display: "flex",
    flexDirection: "row-reverse",
  },
}));

export const MessageItem: FC<IMessageItemProps> = React.memo(
  ({ message, makeSeen }) => {
    const {
      text,
      isMine,
      isSeen,
      created_at_parsed,
      authorPhoto,
      attachments,
    } = message;
    const [isRead, setIsRead] = useState(isSeen);
    const ref = useRef<any>(null);
    const classes = useStyles();
    const timeoutRef = useRef<any>(null);
    const { user } = useAuth();
    const isAdmin = [UserRole.Owner, UserRole.Admin].includes(user.role);

    const changeSeenStatus = () => {
      if (!isAdmin && !isSeen && !isMine) {
        setIsRead(true);
        makeSeen(message);
      }
    };

    const observer = useMemo(
      () =>
        new IntersectionObserver(
          ([entry]) => {
            if (entry.isIntersecting) {
              timeoutRef.current = setTimeout(() => {
                changeSeenStatus();
              }, 1000);
            } else {
              clearTimeout(timeoutRef.current);
            }
          },
          {
            threshold: 1,
          },
        ),
      [],
    );

    useEffect(() => {
      if (ref.current) {
        observer.observe(ref!.current);
      }

      return () => {
        clearTimeout(timeoutRef.current);
        observer.disconnect();
      };
    }, [ref, observer]);

    return (
      <div className={classes.root}>
        <div
          className={clsx(classes.message, isMine && classes.myMessage)}
          onClick={changeSeenStatus}
        >
          <BlinkingDot isVisible={!isRead && !isMine}>
            <Avatar ref={ref} src={authorPhoto} className={classes.avatar} />
          </BlinkingDot>
          <div
            className={clsx(
              classes.groupContainer,
              isMine && classes.myGroupContainer,
            )}
          >
            {!!attachments.length &&
              attachments.map((file) => (
                <div
                  key={file.path}
                  className={clsx(
                    classes.groupSubContainer,
                    isMine && classes.myGroupSubContainer,
                  )}
                >
                  <div
                    className={clsx(
                      classes.template,
                      classes.groupItem,
                      isMine && classes.myGroupItem,
                    )}
                  >
                    <div className={classes.fileContainer}>
                      <FontIcon
                        name="file"
                        className={clsx(
                          classes.fileIcon,
                          isMine && classes.myFileIcon,
                        )}
                      />
                      <div>
                        <div className={classes.fileName}>
                          <Link
                            href={urlNormalizer(file.path)}
                            target="_blank"
                            underline="none"
                          >
                            <OverflowTooltip text={file.name} />
                          </Link>
                        </div>
                        <div
                          className={clsx(
                            classes.fileSize,
                            isMine && classes.myFileSize,
                          )}
                        >
                          {getFileSize(file.size)}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            {!!text && (
              <div
                className={clsx(
                  classes.groupSubContainer,
                  isMine && classes.myGroupSubContainer,
                )}
              >
                <div
                  className={clsx(
                    classes.template,
                    classes.groupItem,
                    isMine && classes.myGroupItem,
                  )}
                >
                  {text}
                </div>
              </div>
            )}
          </div>
        </div>
        <div className={clsx(classes.date, isMine && classes.myDate)}>
          {created_at_parsed}
        </div>
      </div>
    );
  },
);
